import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import PlaceOutlinedIcon from '@material-ui/icons/PlaceOutlined';

import { HeroSection } from '../sections';
import { Card, Heading, LoadingSpinner, PageContainer, Section } from '../../../shared/ui';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import {
  fetchYowanPublicClassList,
  fetchYowanPublicSeminarList,
  getYowanClassIntroText,
  hydrateYowanPublicClassDetails,
  hydrateYowanPublicSeminarDetails,
  summarizeYowanClassSchedules,
} from '../app/yowanContentClient';
const { yowanPageContent } = require('./pageContent');
const { getYowanRouteMeta } = require('../routes/manifest');

const YOWAN_SEMINAR_FALLBACK_LOGO = '/yowan/yowan_logo_bl.jpg';

const resolveInitialFamilyTab = (pathname = '') => (
  pathname === '/seminars' ? 'seminars' : 'classes'
);

const ClassesPage = () => {
  const content = yowanPageContent['/classes'];
  const location = useLocation();
  const initialTab = resolveInitialFamilyTab(location.pathname);
  const metadata = getYowanRouteMeta(initialTab === 'seminars' ? '/seminars' : '/classes');
  const [activeTab, setActiveTab] = useState(initialTab);
  const [classRows, setClassRows] = useState([]);
  const [seminarRows, setSeminarRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    setActiveTab(resolveInitialFamilyTab(location.pathname));
  }, [location.pathname]);

  useEffect(() => {
    if (!metadata) {
      return;
    }

    document.title = metadata.title;

    if (metadata.description) {
      const descriptionTag = document.querySelector('meta[name="description"]');
      if (descriptionTag) {
        descriptionTag.setAttribute('content', metadata.description);
      }
    }
  }, [metadata]);

  useEffect(() => {
    let isMounted = true;

    setIsLoading(true);
    setLoadError('');

    Promise.all([
      fetchYowanPublicClassList({ pageSize: 30 }),
      fetchYowanPublicSeminarList({ pageSize: 30 }),
    ])
      .then(async ([classPayload, seminarPayload]) => {
        if (!isMounted) {
          return;
        }

        const hydratedClasses = await hydrateYowanPublicClassDetails(classPayload.results || [], {
          concurrency: 4,
        });
        const hydratedSeminars = await hydrateYowanPublicSeminarDetails(seminarPayload.results || [], {
          concurrency: 4,
        });
        if (!isMounted) {
          return;
        }

        setClassRows(hydratedClasses);
        setSeminarRows(hydratedSeminars);
      })
      .catch(() => {
        if (!isMounted) {
          return;
        }

        setClassRows([]);
        setSeminarRows([]);
        setLoadError('教室・講座情報を読み込めませんでした。時間をおいて再度お試しください。');
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const tabs = [
    {
      id: 'classes',
      label: content.classes.title,
      lead: content.classes.lead,
      cards: classRows,
      variant: 'classes',
    },
    {
      id: 'seminars',
      label: content.seminars.title,
      lead: content.seminars.lead,
      cards: seminarRows,
      variant: 'seminars',
    },
  ];

  const activeFamily = tabs.find((tab) => tab.id === activeTab) || tabs[0];

  return (
    <>
      <HeroSection {...content.hero} />
      <Section>
        <PageContainer>
          <div className="yowan-classes-intro">
            <p className="yowan-classes-intro__lead">{content.intro.lead}</p>
            <p className="yowan-classes-intro__support">
              {content.intro.supportPrefix}
              <Link
                className="yowan-classes-intro__link"
                to={appendYowanPreviewQueryIfNeeded(content.intro.supportHref, location.search)}
              >
                {content.intro.supportLabel}
              </Link>
              {content.intro.supportSuffix}
            </p>
          </div>
        </PageContainer>
      </Section>
      <Section>
        <PageContainer>
          <div className="yowan-classes-tabs" role="tablist" aria-label="教室・講座の種別">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.id}
                className={`yowan-classes-tabs__tab${
                  activeTab === tab.id ? ' yowan-classes-tabs__tab--active' : ''
                }`}
                onClick={() => setActiveTab(tab.id)}
              >
                <span className="yowan-classes-tabs__label">{tab.label}</span>
              </button>
            ))}
          </div>
        </PageContainer>
      </Section>
      <ClassFamilySection
        key={activeFamily.id}
        variant={activeFamily.variant}
        title={activeFamily.label}
        lead={activeFamily.lead}
        cards={activeFamily.cards}
        isLoading={isLoading}
        loadError={loadError}
        currentSearch={location.search}
      />
    </>
  );
};

const ClassFamilySection = ({ variant, title, lead, cards, isLoading, loadError, currentSearch }) => {
  return (
    <Section>
      <PageContainer>
        <div className={`yowan-family-section yowan-family-section--${variant}`}>
          <Heading as="h2">{title}</Heading>
          <p className="yowan-family-section__lead">{lead}</p>
          {isLoading ? (
            <LoadingSpinner label="教室・講座情報を読み込み中" showLabel />
          ) : null}
          {!isLoading && loadError ? (
            <p className="yowan-family-section__teaser">{loadError}</p>
          ) : null}
          {!isLoading && !loadError && !cards.length ? (
            <p className="yowan-family-section__teaser">現在公開中の情報はありません。</p>
          ) : null}
          <div className="yowan-family-section__grid">
            {cards.map((card) => (
              variant === 'classes' ? (
                <ClassCard key={card.id} card={card} currentSearch={currentSearch} />
              ) : (
                <SeminarCard key={card.id} card={card} currentSearch={currentSearch} />
              )
            ))}
          </div>
        </div>
      </PageContainer>
    </Section>
  );
};

const getSeminarCardText = (card) => {
  const text = card.seriesKind === 'grouped'
    ? card.summary || card.series?.summary || ''
    : card.summary || '';
  return text
    .replace(/\s+/g, ' ')
    .trim();
};

const getSeminarCardImage = (card) => {
  const ownImage = card.image || card.primary_image?.preview_url || card.primary_image?.url || '';
  const seriesImage = card.series?.image || card.series?.primary_image?.preview_url || card.series?.primary_image?.url || '';
  const directImage = card.seriesKind === 'grouped'
    ? seriesImage || ownImage
    : ownImage;
  const nestedSources = [
    ...(card.events || []),
    ...(card.seminars || []),
    ...(card.series?.seminars || []),
    card.firstEvent,
  ].filter(Boolean);
  const nestedImage = nestedSources
    .map((item) => item.image || item.primary_image?.preview_url || item.primary_image?.url || '')
    .find(Boolean);

  if (directImage || nestedImage) {
    return {
      src: directImage || nestedImage,
      isFallback: false,
    };
  }

  return {
    src: YOWAN_SEMINAR_FALLBACK_LOGO,
    isFallback: true,
  };
};

const hasSeminarCardScheduleData = (item = {}) => (
  Boolean(item.datetime || item.location || item.instructor || item.fee)
);

const uniqueSeminarValues = (values = []) => (
  [...new Set(values.map((value) => String(value || '').trim()).filter(Boolean))]
);

const appendOtherLabel = (value, shouldAppend) => (
  value && shouldAppend ? `${value} 他` : value
);

const getSeminarCardSlots = (card = {}) => {
  const sectionSlots = (card.seminarSections || []).flatMap((section) => section.slots || []);
  const directSlots = card.slots || [];
  const fallbackSlots = [card.nextSlot, card.firstSlot].filter(Boolean);
  const seen = new Set();

  return [...sectionSlots, ...directSlots, ...fallbackSlots].filter((slot, index) => {
    if (!slot) {
      return false;
    }

    const key = slot.id || `${slot.datetimeLabel || ''}:${slot.title || ''}:${index}`;

    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};

const getSeminarSlotApplicationStatus = (slot = {}) => {
  if (slot.canApply === true || slot.can_apply === true) {
    return 'open';
  }

  return slot.applicationStatus || slot.application_status || (slot.isOpen === false ? 'closed' : '');
};

const getSeminarApplicationBadge = (card = {}) => {
  const slots = getSeminarCardSlots(card);
  const statuses = slots.map(getSeminarSlotApplicationStatus).filter(Boolean);

  if (!slots.length || !statuses.length) {
    return null;
  }

  if (statuses.includes('open')) {
    return { label: '受付中', status: 'open' };
  }

  if (statuses.every((status) => status === 'ended')) {
    return { label: '開催終了', status: 'ended' };
  }

  if (statuses.includes('closed')) {
    return { label: '受付停止中', status: 'closed' };
  }

  return null;
};

const getSeminarFeeSummary = (slots = [], fullParticipationFeeLabel = '') => {
  const hasMultipleSlots = slots.length > 1;
  const slotFeeLabels = uniqueSeminarValues(
    slots.map((slot) => {
      if (!slot.feeLabel || slot.feeLabel === '未定') {
        return '';
      }

      return hasMultipleSlots ? `${slot.feeLabel}/一コマ` : slot.feeLabel;
    }),
  );
  const feeParts = [];

  if (slotFeeLabels.length) {
    feeParts.push(slotFeeLabels.join(' / '));
  }

  if (fullParticipationFeeLabel && fullParticipationFeeLabel !== '未定') {
    feeParts.push(`通し ${fullParticipationFeeLabel}`);
  }

  return feeParts.join(' / ');
};

const buildSeminarScheduleItem = ({
  id,
  title = '',
  slots = [],
  fullParticipationFee = '',
  fullParticipationFeeLabel = '',
  fallback = {},
}) => {
  const meaningfulSlots = slots.filter((slot) => (
    slot.datetimeLabel || slot.locationLabel || slot.instructorName || slot.feeLabel
  ));
  const sourceSlot = meaningfulSlots[0] || fallback;
  const hasMultipleSlots = meaningfulSlots.length > 1;
  const instructorNames = uniqueSeminarValues([
    ...meaningfulSlots.map((slot) => slot.instructorName),
    fallback.instructorName,
  ]);
  const seminarFullFeeLabel = fullParticipationFee ? fullParticipationFeeLabel : '';
  const fee = getSeminarFeeSummary(meaningfulSlots, seminarFullFeeLabel) ||
    (fallback.feeLabel && fallback.feeLabel !== '未定' ? fallback.feeLabel : '');

  return {
    id: id || sourceSlot.id,
    title,
    datetime: appendOtherLabel(sourceSlot.datetimeLabel || fallback.datetimeLabel || '', hasMultipleSlots),
    location: appendOtherLabel(sourceSlot.locationLabel || fallback.locationLabel || '', hasMultipleSlots),
    instructor: instructorNames.join(' / '),
    fee,
  };
};

const getSeminarCardScheduleItems = (card, { limit = 3 } = {}) => {
  const sectionItems = (card.seminarSections || []).map((section) => (
    buildSeminarScheduleItem({
      id: section.id || `${card.id}-seminar-section`,
      title: section.title || '',
      slots: section.slots || [],
      fullParticipationFee: section.seminarFullParticipationFee || '',
      fullParticipationFeeLabel: section.seminarFullParticipationFeeLabel || '',
    })
  ));
  const nextSlot = card.nextSlot || {};
  const fallbackItem = {
    id: nextSlot.id || `${card.id}-representative-slot`,
    title: nextSlot.title || '',
    datetime: nextSlot.datetimeLabel || card.cardDatetime || card.nextSlotLabel || card.datetime || '',
    location: nextSlot.locationLabel || card.cardLocation || card.area || '',
    instructor: nextSlot.instructorName || card.cardInstructor || card.standardInstructor || '',
    fee: nextSlot.feeLabel && nextSlot.feeLabel !== '未定'
      ? nextSlot.feeLabel
      : card.cardFee || '',
  };
  const slotSummaryItem = buildSeminarScheduleItem({
    id: `${card.id}-slot-summary`,
    slots: card.slots || [],
    fullParticipationFee: card.seminarFullParticipationFee || '',
    fullParticipationFeeLabel: card.seminarFullParticipationFeeLabel || '',
    fallback: fallbackItem,
  });
  const meaningfulSectionItems = sectionItems.filter(hasSeminarCardScheduleData);
  const meaningfulSlotItems = [slotSummaryItem].filter(hasSeminarCardScheduleData);
  const fallbackItems = [fallbackItem].filter(hasSeminarCardScheduleData);
  const items = meaningfulSectionItems.length
    ? meaningfulSectionItems
    : meaningfulSlotItems.length
      ? meaningfulSlotItems
      : fallbackItems;

  return {
    items: items.slice(0, limit),
    remainingCount: Math.max(items.length - limit, 0),
  };
};

const SeminarCard = ({ card, currentSearch }) => {
  const cardImage = getSeminarCardImage(card);
  const imageSrc = cardImage.src;
  const detailHref = card.href ? appendYowanPreviewQueryIfNeeded(card.href, currentSearch) : '';
  const hasImage = Boolean(imageSrc);
  const introText = getSeminarCardText(card);
  const scheduleSummary = getSeminarCardScheduleItems(card);
  const feeLabel = card.fullParticipationFeeLabel && card.fullParticipationFeeLabel !== '未定'
    ? card.fullParticipationFeeLabel
    : '';
  const applicationBadge = getSeminarApplicationBadge(card);

  return (
    <Card className={`yowan-family-section__card yowan-family-section__card--box yowan-family-section__card--seminar${
      hasImage ? ' yowan-family-section__card--has-image' : ''
    }`}
    >
      {hasImage ? (
        detailHref ? (
          <Link
            className={`yowan-family-section__image-link${
              cardImage.isFallback ? ' yowan-family-section__image-link--logo' : ''
            }`}
            to={detailHref}
            aria-label={`${card.title}の詳細を見る`}
          >
            <img
              className={`yowan-family-section__image${
                cardImage.isFallback ? ' yowan-family-section__image--logo' : ''
              }`}
              src={imageSrc}
              alt={cardImage.isFallback ? '揺腕法ロゴ' : card.title}
              loading="lazy"
              decoding="async"
            />
          </Link>
        ) : (
          <div className={`yowan-family-section__image-link${
            cardImage.isFallback ? ' yowan-family-section__image-link--logo' : ''
          }`}
          >
            <img
              className={`yowan-family-section__image${
                cardImage.isFallback ? ' yowan-family-section__image--logo' : ''
              }`}
              src={imageSrc}
              alt={cardImage.isFallback ? '揺腕法ロゴ' : card.title}
              loading="lazy"
              decoding="async"
            />
          </div>
        )
      ) : null}
      <div className="yowan-family-section__content">
        <div className="yowan-family-section__title-stack">
          <Heading as="h3">{card.title}</Heading>
          {card.subtitle ? (
            <p className="yowan-family-section__subtitle">{card.subtitle}</p>
          ) : null}
          <div className="yowan-family-section__badges" aria-label="セミナーの概要">
            {applicationBadge ? (
              <span className={`yowan-family-section__badge yowan-family-section__badge--application yowan-family-section__badge--application-${applicationBadge.status}`}>
                {applicationBadge.label}
              </span>
            ) : null}
          </div>
        </div>
        {introText ? (
          <p className="yowan-family-section__summary">{introText}</p>
        ) : null}
        <div className="yowan-family-section__schedule-list">
          {scheduleSummary.items.length ? scheduleSummary.items.map((schedule) => (
            <div key={schedule.id || schedule.datetime} className="yowan-family-section__schedule">
              {schedule.title ? (
                <p className="yowan-family-section__schedule-title">{schedule.title}</p>
              ) : null}
              {schedule.datetime ? (
                <p className="yowan-family-section__datetime">
                  <CalendarTodayIcon className="yowan-family-section__data-icon" aria-hidden="true" />
                  <span>{schedule.datetime}</span>
                </p>
              ) : null}
              {schedule.location ? (
                <p className="yowan-family-section__area">
                  <PlaceOutlinedIcon className="yowan-family-section__data-icon" aria-hidden="true" />
                  <span>{schedule.location}</span>
                </p>
              ) : null}
              <div className="yowan-family-section__meta">
                {schedule.instructor ? (
                  <span>
                    <span className="yowan-family-section__data-label" aria-hidden="true">講師</span>
                    <span>{schedule.instructor}</span>
                  </span>
                ) : null}
                {schedule.fee ? (
                  <span>
                    <span className="yowan-family-section__data-label" aria-hidden="true">参加費</span>
                    <span>{schedule.fee}</span>
                  </span>
                ) : null}
              </div>
            </div>
          )) : (
            <div className="yowan-family-section__schedule">
              <p className="yowan-family-section__datetime">
                <CalendarTodayIcon className="yowan-family-section__data-icon" aria-hidden="true" />
                <span>{card.datetime || '開催日時は詳細で確認'}</span>
              </p>
            </div>
          )}
          {scheduleSummary.remainingCount > 0 ? (
            <p className="yowan-family-section__more">ほか{scheduleSummary.remainingCount}件の開催枠</p>
          ) : null}
        </div>
        {feeLabel ? (
          <p className="yowan-family-section__series-fee">
            <span className="yowan-family-section__data-label" aria-hidden="true">通し参加費</span>
            <span>{feeLabel}</span>
          </p>
        ) : null}
      </div>
      <div className="yowan-family-section__footer">
        {detailHref ? (
          <Link
            className="yowan-family-section__link"
            to={detailHref}
          >
            {card.ctaLabel || '詳細を見る'}
          </Link>
        ) : (
          <p className="yowan-family-section__teaser">{card.teaser || '詳細ページは準備中'}</p>
        )}
      </div>
    </Card>
  );
};

const ClassCard = ({ card, currentSearch }) => {
  const scheduleSummary = summarizeYowanClassSchedules(card, { limit: 2 });
  const introText = getYowanClassIntroText(card);
  const imageSrc = card.image || card.primary_image?.preview_url || card.primary_image?.url || '';
  const detailHref = card.href ? appendYowanPreviewQueryIfNeeded(card.href, currentSearch) : '';
  const hasImage = Boolean(imageSrc && detailHref);

  return (
    <Card className={`yowan-family-section__card yowan-family-section__card--box${
      hasImage ? ' yowan-family-section__card--has-image' : ''
    }`}
    >
      {hasImage ? (
        <Link
          className="yowan-family-section__image-link"
          to={detailHref}
          aria-label={`${card.title}の詳細を見る`}
        >
          <img
            className="yowan-family-section__image"
            src={imageSrc}
            alt={card.title}
            loading="lazy"
            decoding="async"
          />
        </Link>
      ) : null}
      <div className="yowan-family-section__content">
        <Heading as="h3">{card.title}</Heading>
        {introText ? (
          <p className="yowan-family-section__summary">{introText}</p>
        ) : null}
        {scheduleSummary.items.length ? (
          <div className="yowan-family-section__schedule-list">
            {scheduleSummary.items.map((schedule) => (
              <div key={schedule.id} className="yowan-family-section__schedule">
                {schedule.datetime ? (
                  <p className="yowan-family-section__datetime">
                    <CalendarTodayIcon className="yowan-family-section__data-icon" aria-hidden="true" />
                    <span>{schedule.datetime}</span>
                  </p>
                ) : null}
                {schedule.location ? (
                  <p className="yowan-family-section__area">
                    <PlaceOutlinedIcon className="yowan-family-section__data-icon" aria-hidden="true" />
                    <span>{schedule.location}</span>
                  </p>
                ) : null}
                <div className="yowan-family-section__meta">
                  {schedule.instructor ? (
                    <span>
                      <span className="yowan-family-section__data-label" aria-hidden="true">講師</span>
                      <span>{schedule.instructor}</span>
                    </span>
                  ) : null}
                  {schedule.fee ? (
                    <span>
                      <span className="yowan-family-section__data-label" aria-hidden="true">参加費</span>
                      <span>1回 {schedule.fee}</span>
                    </span>
                  ) : null}
                </div>
              </div>
            ))}
            {scheduleSummary.remainingCount > 0 ? (
              <p className="yowan-family-section__more">ほか{scheduleSummary.remainingCount}件の開催情報</p>
            ) : null}
          </div>
        ) : null}
      </div>
      <div className="yowan-family-section__footer">
        {detailHref ? (
          <Link
            className="yowan-family-section__link"
            to={detailHref}
          >
            {card.ctaLabel || '詳細を見る'}
          </Link>
        ) : (
          <p className="yowan-family-section__teaser">{card.teaser || '詳細ページは準備中'}</p>
        )}
      </div>
    </Card>
  );
};

export default ClassesPage;
